/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { TypedEventEmitter } from '../emit/typed-event-emitter';
import type { BaseRequestOpts, Body, HttpApiEventHandlerMap, IHttpOpts, IRequestOpts, QueryDict } from './interface';
import type { Method } from './method';
import { HttpApiEvent } from '../@types/http-api';
import { checkObjectHasKeys, deepCopy, encodeParams } from '../utils';
import { ConnectionError, TokenRefreshError, XHubChatError } from './error';
// eslint-disable-next-line sort-imports
import { TokenRefresher, TokenRefreshOutcome } from './refetch';
import { RequestQueue } from './request-queue';
import { anySignal, parseErrorResponse, timeoutSignal } from './utils';

export class FetchHttpApi<O extends IHttpOpts> {
  private abortController = new AbortController();
  private readonly tokenRefresher: TokenRefresher;
  private readonly requestQueue: RequestQueue;
  private isWaitingForToken = false;

  public constructor(
    private eventEmitter: TypedEventEmitter<HttpApiEvent, HttpApiEventHandlerMap>,
    public readonly opts: O,
  ) {
    checkObjectHasKeys(opts, ['baseUrl', 'prefix']);
    if (!opts.onlyData) {
      throw new Error('Constructing FetchHttpApi without `onlyData=true` is no longer supported.');
    }
    opts.useAuthorizationHeader = opts.useAuthorizationHeader ?? true;

    this.tokenRefresher = new TokenRefresher(opts);
    this.requestQueue = new RequestQueue({
      logger: opts.logger,
      maxQueueSize: 100,
      maxQueueTime: 5 * 60 * 1000,
    });
  }

  /**
   * Fetch wrapper that uses the provided fetch function or falls back to globalThis.fetch.
   * @param resource - URL or string to fetch
   * @param options - Fetch options
   * @returns A promise that resolves to the fetch response
   */
  public fetch(resource: URL | string, options?: RequestInit): ReturnType<typeof globalThis.fetch> {
    // If a custom fetch function is provided in options, use it
    if (this.opts.fetchFn) {
      return this.opts.fetchFn(resource, options);
    }
    return globalThis.fetch(resource, options);
  }

  /**
   * Aborts all ongoing requests and resets the abort controller.
   */
  public abort(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  /**
   * Handle token expiration by queueing the request and emitting TokenExpired event.
   * This is used when no tokenRefreshFunction is provided - the application must
   * refresh the token manually and call setAccessToken().
   */
  private handleTokenExpiredWithQueue<T>(
    method: Method,
    path: string,
    queryParams?: QueryDict,
    body?: Body,
    opts?: IRequestOpts,
  ): Promise<T> {
    this.opts.logger?.warn('Access token expired. Queueing request and waiting for new token...');

    // Emit TokenExpired event only once
    if (!this.isWaitingForToken) {
      this.isWaitingForToken = true;
      this.eventEmitter.emit(HttpApiEvent.TokenExpired);
      this.opts.logger?.debug('Emitted TokenExpired event');
    }

    // Queue the request for retry after token refresh
    return this.requestQueue.enqueue<T>(method, path, queryParams, body, opts);
  }

  /**
   * Set a new access token and retry all queued requests.
   * This should be called by the application after refreshing the token.
   * @param accessToken - The new access token
   */
  public setAccessToken(accessToken: string): void {
    this.opts.accessToken = accessToken;
    this.opts.logger?.debug(`Access token updated, retrying ${this.requestQueue.size()} queued requests...`);

    // Reset waiting flag
    this.isWaitingForToken = false;

    // Retry all queued requests
    this.retryQueuedRequests();
  }

  /**
   * Retry all queued requests with the new token
   */
  private async retryQueuedRequests(): Promise<void> {
    const requests = this.requestQueue.getAll();

    if (requests.length === 0) {
      this.opts.logger?.debug('No queued requests to retry');
      return;
    }

    this.opts.logger?.debug(`Retrying ${requests.length} queued requests...`);

    for (const queuedRequest of requests) {
      try {
        // Check if request can still be retried
        if (!this.requestQueue.canRetry(queuedRequest)) {
          this.requestQueue.remove(queuedRequest.id);
          queuedRequest.reject(new Error('Max retry attempts exceeded'));
          continue;
        }

        this.requestQueue.incrementRetryCount(queuedRequest.id);

        // Retry the request
        const response = await this.authedRequest(
          queuedRequest.method,
          queuedRequest.path,
          queuedRequest.queryParams,
          queuedRequest.body,
          queuedRequest.opts,
        );

        // Request succeeded, remove from queue and resolve
        this.requestQueue.remove(queuedRequest.id);
        queuedRequest.resolve(response);
        this.opts.logger?.debug(`Successfully retried request ${queuedRequest.id}`);
      } catch (error) {
        // Request failed again
        if (error instanceof XHubChatError && error.errcode === 'M_UNKNOWN_TOKEN') {
          // Token still invalid, keep in queue
          this.opts.logger?.warn(`Request ${queuedRequest.id} failed again with invalid token`);
          continue;
        }

        // Other error, remove from queue and reject
        this.requestQueue.remove(queuedRequest.id);
        queuedRequest.reject(error as Error);
        this.opts.logger?.error(`Failed to retry request ${queuedRequest.id}:`, error);
      }
    }

    // Clean up any expired requests
    this.requestQueue.cleanExpired();
  }

  /**
   * Get the current request queue statistics
   */
  public getQueueStats(): ReturnType<RequestQueue['getStats']> {
    return this.requestQueue.getStats();
  }

  /**
   * Clear all queued requests
   */
  public clearQueue(reason?: string): void {
    this.requestQueue.clear(reason);
    this.isWaitingForToken = false;
  }

  /**
   * Perform a request to the backend server without any credentials.
   * @param method - The HTTP method e.g. "GET".
   * @param path - The HTTP path <b>after</b> the supplied prefix e.g.
   * "/createRoom".
   * @param queryParams - A dict of query params (these will NOT be
   * urlencoded). If unspecified, there will be no query params.
   * @param body - The HTTP JSON body.
   * @param opts - Additional request options.
   * @returns The parsed response.
   * @throws Error if a problem occurred. This includes network problems and XHubChat-specific error JSON.
   */
  public request<T>(
    method: Method,
    path: string,
    queryParams?: QueryDict,
    body?: Body,
    opts?: IRequestOpts,
  ): Promise<T> {
    const fullUri = this.getUrl(path, queryParams, opts?.prefix, opts?.baseUrl);
    return this.requestOtherUrl<T>(method, fullUri, body, opts);
  }

  /**
   * Perform a request to an arbitrary URL.
   * @param method - The HTTP method e.g. "GET".
   * @param url - The HTTP URL object.
   * @param body - The HTTP JSON body.
   * @param opts - Additional request options.
   * @returns The parsed response.
   * @throws Error if a problem occurred. This includes network problems and XHubChat-specific error JSON.
   */
  public async requestOtherUrl<T>(
    method: Method,
    url: URL | string,
    body?: Body,
    opts: BaseRequestOpts = {},
  ): Promise<T> {
    if (opts.json !== undefined && opts.rawResponseBody !== undefined) {
      throw new Error('Invalid call to `FetchHttpApi` sets both `opts.json` and `opts.rawResponseBody`');
    }

    const urlForLogs = this.sanitizeUrlForLogs(url);

    this.opts.logger?.debug(`FetchHttpApi: --> ${method} ${urlForLogs}`);

    const headers = Object.assign({}, opts.headers || {});

    const jsonResponse = !opts.rawResponseBody && opts.json !== false;
    if (jsonResponse) {
      if (!headers.Accept) {
        headers.Accept = 'application/json';
      }
    }

    const timeout = opts.localTimeoutMs ?? this.opts.localTimeoutMs;
    const keepAlive = opts.keepAlive ?? false;
    const signals = [this.abortController.signal];
    if (timeout !== undefined) {
      signals.push(timeoutSignal(timeout));
    }
    if (opts.abortSignal) {
      signals.push(opts.abortSignal);
    }

    // If the body is an object, encode it as JSON and set the `Content-Type` header,
    // unless that has been explicitly inhibited by setting `opts.json: false`.
    // We can't use getPrototypeOf here as objects made in other contexts e.g. over postMessage won't have same ref
    let data: BodyInit;
    if (opts.json !== false && body?.constructor?.name === Object.name) {
      data = JSON.stringify(body);
      if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }
    } else {
      data = body as unknown as BodyInit;
    }

    const { signal, cleanup } = anySignal(signals);

    let res: Response;
    const start = Date.now();
    try {
      res = await this.fetch(url, {
        signal,
        method,
        body: data,
        headers,
        mode: 'cors',
        redirect: 'follow',
        referrer: '',
        referrerPolicy: 'no-referrer',
        cache: 'no-cache',
        credentials: 'omit', // we send credentials via headers
        keepalive: keepAlive,
        priority: opts.priority,
      });

      this.opts.logger?.debug(
        `FetchHttpApi: <-- ${method} ${urlForLogs} [${Date.now() - start}ms ${res.status}]`,
      );
    } catch (e) {
      this.opts.logger?.debug(`FetchHttpApi: <-- ${method} ${urlForLogs} [${Date.now() - start}ms ${e}]`);
      if ((<Error>e).name === 'AbortError') {
        throw e;
      }
      throw new ConnectionError('fetch failed', <Error>e);
    } finally {
      cleanup();
    }

    if (!res.ok) {
      throw parseErrorResponse(res, await res.text());
    }

    if (opts.rawResponseBody) {
      return (await res.blob()) as T;
    } else if (jsonResponse) {
      return await res.json();
    } else {
      return (await res.text()) as T;
    }
  }

  private sanitizeUrlForLogs(url: URL | string): string {
    try {
      let asUrl: URL;
      if (typeof url === 'string') {
        asUrl = new URL(url);
      } else {
        asUrl = url;
      }
      // Remove the values of any URL params that could contain potential secrets
      const sanitizedQs = new URLSearchParams();
      for (const key of asUrl.searchParams.keys()) {
        sanitizedQs.append(key, 'xxx');
      }
      const sanitizedQsString = sanitizedQs.toString();
      const sanitizedQsUrlPiece = sanitizedQsString ? `?${sanitizedQsString}` : '';

      return asUrl.origin + asUrl.pathname + sanitizedQsUrlPiece;
    } catch {
      // defensive coding for malformed url
      return '??';
    }
  }

  /**
   * Perform an authorized request to the homeserver.
   * @param method - The HTTP method e.g. "GET".
   * @param path - The HTTP path <b>after</b> the supplied prefix e.g.
   * "/createRoom".
   *
   * @param queryParams - A dict of query params (these will NOT be
   * urlencoded). If unspecified, there will be no query params.
   *
   * @param body - The HTTP JSON body.
   *
   * @param paramOpts - Additional request options.
   * When `paramOpts.doNotAttemptTokenRefresh` is true, token refresh will not be attempted
   * when an expired token is encountered. Used to only attempt token refresh once.
   *
   * @returns The parsed response.
   * @throws Error if a problem occurred. This includes network problems and XHubChat-specific error JSON.
   */
  public authedRequest<T>(
    method: Method,
    path: string,
    queryParams: QueryDict = {},
    body?: Body,
    paramOpts: IRequestOpts = {},
  ): Promise<T> {
    return this.doAuthedRequest<T>(1, method, path, queryParams, body, paramOpts);
  }

  // Wrapper around public method authedRequest to allow for tracking retry attempt counts
  private async doAuthedRequest<T>(
    attempt: number,
    method: Method,
    path: string,
    queryParams: QueryDict,
    body?: Body,
    paramOpts: IRequestOpts = {},
  ): Promise<T> {
    // avoid mutating paramOpts so they can be used on retry
    const opts = deepCopy(paramOpts);
    // we have to manually copy the abortSignal over as it is not a plain object
    opts.abortSignal = paramOpts.abortSignal;

    // Take a snapshot of the current token state before we start the request so we can reference it if we error
    const requestSnapshot = await this.tokenRefresher.prepareForRequest();
    if (requestSnapshot.accessToken) {
      if (this.opts.useAuthorizationHeader) {
        if (!opts.headers) {
          opts.headers = {};
        }
        if (!opts.headers.Authorization) {
          opts.headers.Authorization = `Bearer ${requestSnapshot.accessToken}`;
        }
        if (queryParams.access_token) {
          delete queryParams.access_token;
        }
      } else if (!queryParams.access_token) {
        queryParams.access_token = requestSnapshot.accessToken;
      }
    }

    try {
      const response = await this.request<T>(method, path, { ...queryParams, ...this.opts.clientParams }, body, opts);
      return response;
    } catch (error) {
      if (!(error instanceof XHubChatError)) {
        throw error;
      }

      if (error.errcode === 'M_UNKNOWN_TOKEN') {
        // If tokenRefreshFunction is NOT provided, use queue-based approach
        if (!this.opts.tokenRefreshFunction) {
          return this.handleTokenExpiredWithQueue<T>(method, path, queryParams, body, paramOpts);
        }

        // If tokenRefreshFunction IS provided, use the existing auto-refresh approach
        const outcome = await this.tokenRefresher.handleUnknownToken(requestSnapshot, attempt);
        if (outcome === TokenRefreshOutcome.Success) {
          // if we got a new token retry the request
          return this.doAuthedRequest(attempt + 1, method, path, queryParams, body, paramOpts);
        }
        if (outcome === TokenRefreshOutcome.Failure) {
          throw new TokenRefreshError(error);
        }

        if (!opts?.inhibitLogoutEmit) {
          this.eventEmitter.emit(HttpApiEvent.SessionLoggedOut, error);
        }
      } else if (error.errcode === 'M_CONSENT_NOT_GIVEN') {
        this.eventEmitter.emit(HttpApiEvent.NoConsent, error.message, error.data.consent_uri);
      }

      throw error;
    }
  }

  /**
   * Form and return a homeserver request URL based on the given path params and prefix.
   * @param path - The HTTP path <b>after</b> the supplied prefix e.g. "/createRoom".
   * @param queryParams - A dict of query params (these will NOT be urlencoded).
   * @param prefix - The full prefix to use e.g. "/_matrix/client/v2_alpha", defaulting to this.opts.prefix.
   * @param baseUrl - The baseUrl to use e.g. "https://matrix.org", defaulting to this.opts.baseUrl.
   * @returns URL
   */
  public getUrl(path: string, queryParams?: QueryDict, prefix?: string, baseUrl?: string): URL {
    const baseUrlWithFallback = baseUrl ?? this.opts.baseUrl;
    const baseUrlWithoutTrailingSlash = baseUrlWithFallback.endsWith('/')
      ? baseUrlWithFallback.slice(0, -1)
      : baseUrlWithFallback;
    const url = new URL(baseUrlWithoutTrailingSlash + (prefix ?? this.opts.prefix) + path);
    // If there are any params, encode and append them to the URL.
    if (queryParams) {
      const mergedParams = { ...queryParams };
      encodeParams(mergedParams, url.searchParams);
    }
    return url;
  }
}
